import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function IntermediateTestDialog({ open, onOpenChange, moduleId, courseQuestions, existing, onSaved, onDeleted }) {
  const [selected, setSelected] = useState(new Set());
  const [passingScore, setPassingScore] = useState(70);
  // Quante domande somministrare, estratte a sorte dal pool a ogni tentativo.
  // Vuoto = tutte quelle selezionate (comportamento dei test creati prima che
  // il campo esistesse).
  const [perAttempt, setPerAttempt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setSelected(new Set(existing.question_ids));
      setPassingScore(existing.passing_score || 70);
      setPerAttempt(existing.questions_per_attempt ?? "");
    } else {
      setSelected(new Set());
      setPassingScore(70);
      setPerAttempt("");
    }
  }, [open, existing]);

  const toggle = (qid) => {
    const next = new Set(selected);
    if (next.has(qid)) next.delete(qid);
    else next.add(qid);
    setSelected(next);
  };

  const save = async () => {
    if (selected.size === 0) {
      toast.error("Seleziona almeno una domanda");
      return;
    }
    // Campo vuoto = somministra tutte le domande selezionate
    const n = perAttempt === "" ? null : Number(perAttempt);
    if (n !== null && (!n || n < 1)) {
      toast.error("Il numero di domande per tentativo deve essere ≥ 1");
      return;
    }
    if (n !== null && n > selected.size) {
      toast.error("Le domande per tentativo non possono superare quelle selezionate");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/modules/${moduleId}/intermediate-test`, {
        question_ids: Array.from(selected),
        questions_per_attempt: n,
        passing_score: Number(passingScore) || 70,
      });
      toast.success("Test intermedio salvato");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Rimuovere il test intermedio da questo modulo?")) return;
    try {
      await api.delete(`/modules/${moduleId}/intermediate-test`);
      toast.success("Test rimosso");
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="intermediate-test-dialog" className="max-w-2xl rounded-sm max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="wordmark text-brand-blue">Test intermedio del modulo</DialogTitle>
          <DialogDescription>
            Seleziona le domande dal pool del corso: a ogni tentativo ne verranno estratte
            a sorte quelle indicate. Lo studente ha 3 tentativi; esauriti senza successo,
            il progresso del modulo viene azzerato e dovrà rivedere le lezioni.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-1">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Domande estratte per tentativo</Label>
              <Input
                data-testid="it-questions-per-attempt"
                type="number"
                min={1}
                placeholder="tutte"
                value={perAttempt}
                onChange={(e) => setPerAttempt(e.target.value)}
                className="mt-1.5 h-10 rounded-sm"
              />
              <div className="text-xs text-brand-muted mt-1">
                Lascia vuoto per somministrarle tutte.
              </div>
            </div>
            <div>
              <Label>Punteggio minimo per superare</Label>
              <Input
                data-testid="it-passing-score"
                type="number"
                min={1}
                max={100}
                value={passingScore}
                onChange={(e) => setPassingScore(e.target.value)}
                className="mt-1.5 h-10 rounded-sm"
              />
              <div className="text-xs text-brand-muted mt-1">
                Percentuale di risposte corrette.
              </div>
            </div>
            <div className="flex items-start sm:justify-end">
              <div className="text-sm text-brand-muted sm:text-right sm:mt-7">
                Domande selezionate:{" "}
                <span className="font-semibold text-brand-blue" data-testid="it-selected-count">{selected.size}</span> / {courseQuestions.length}
              </div>
            </div>
          </div>

          {/* Con pochi valori possibili la soglia va tarata sul numero di domande:
              con 3 domande, 2 corrette valgono 67%. Meglio dirlo che lasciarlo
              scoprire a uno studente bloccato. */}
          {(() => {
            const n = perAttempt === "" ? selected.size : Number(perAttempt);
            if (!n || n < 1 || !passingScore) return null;
            const minime = Math.ceil((Number(passingScore) / 100) * n);
            if (minime > n) return null;
            return (
              <div
                data-testid="it-threshold-hint"
                className="text-xs text-brand-muted bg-brand-bg border border-brand-line rounded-sm px-3 py-2"
              >
                Con <strong>{n}</strong> {n === 1 ? "domanda" : "domande"} e soglia{" "}
                <strong>{passingScore}%</strong>, lo studente dovrà rispondere correttamente
                ad almeno <strong>{minime}</strong> {minime === 1 ? "domanda" : "domande"}.
              </div>
            );
          })()}

          {courseQuestions.length === 0 ? (
            <div className="text-sm text-brand-muted bg-brand-bg border border-dashed border-brand-line rounded-sm p-6 text-center">
              Nessuna domanda nel pool del corso. Vai alla tab “Pool domande” per crearne.
            </div>
          ) : (
            <div className="space-y-2">
              {courseQuestions.map((q, i) => {
                const isSelected = selected.has(q.id);
                return (
                  <label
                    key={q.id}
                    data-testid={`it-question-${q.id}`}
                    className={
                      "flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors " +
                      (isSelected ? "border-brand-blue bg-brand-blue/5" : "border-brand-line hover:border-brand-blue/40")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(q.id)}
                      className="mt-1 accent-brand-blue"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-widest text-brand-muted">Domanda {i + 1}</div>
                      <div className="text-sm text-brand-ink">{q.text}</div>
                    </div>
                    {isSelected && <CheckCircle2 size={16} className="text-brand-blue shrink-0 mt-0.5" />}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between gap-2 sm:justify-between">
          <div>
            {existing && (
              <button type="button" onClick={remove} data-testid="it-delete-btn" className="text-sm text-red-600 hover:underline underline-offset-4">
                Rimuovi test
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-brand-muted">Annulla</button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="it-save-btn"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Salva
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
